import { Box } from '@uhg-abyss/web/ui/Box';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Card } from '@uhg-abyss/web/ui/Card';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { IconBrand } from '@uhg-abyss/web/ui/IconBrand';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Text } from '@uhg-abyss/web/ui/Text';
import { Tooltip } from '@uhg-abyss/web/ui/Tooltip';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { adobeLinkTrackEvent } from '../../../../../common/AdobeTagging/adobeLinkTrackEvent';
import {
  NEW_TAB_CONFIRMATION,
  PRACTITIONER_NAME_FOR_ANALYTICS,
} from '../../../../../common/Constants';
import { ConstantsLagoon } from '../../../../../common/ConstantsLagoon';
import { StyledDiv } from '../../../../../common/ConstantsStyles';
import { CustomAttributesBlock } from '../../../../../common/Utils/adobeTrackUtils/adobeTrackUtils';
import { SelectedProviderList } from '../../../../../models/Provider';
import { useChipStore } from '../../../../../store/useChipStore';
import { ChipState } from '../../../../../store/useChipStore/chipStore';
import { returnSuppressFlag } from '../../../../../utils/featureSuppress';
import {
  getNameAttrs,
  parseProviderName,
} from '../../../../../utils/providerNames.utils';
import { isVirtualVisitOnlyAddress } from '../../../../../utils/virtualVisitOnly';
import { ConfirmationModal } from '../CompareDrawer/Components/ConfirmationModal/ConfirmationModal';
import {
  ButtonCss,
  LocationIconMobile,
  PlaceHolderText,
  RemoveButtonIcon,
  RemoveButtonText,
  ShareBoxStyledMobile,
  ShareBoxStyledSelectedMobile,
  ShareBoxTextAddressMobile,
  ShareBoxTextNameMobile,
} from './ShareDrawer.styled';

type Props = {
  selectedProviderList: SelectedProviderList;
  selected: number;
  modal: any;
  removeItem: any;
  handleProviderNameAndCarrotClick: any;
  clickedIndex: number;
  customAttributesBlock: CustomAttributesBlock;
  handleClickCb: any;
  index: number;
};
export const CarouselSelectedProviderSlide = ({
  selectedProviderList,
  selected,
  modal,
  removeItem,
  handleProviderNameAndCarrotClick,
  clickedIndex,
  customAttributesBlock,
  handleClickCb,
  index,
}: Props) => {
  const { t } = useTranslation();
  const { chipValue } = useChipStore(
    useShallow((state: ChipState) => ({
      coverageType: state.coverageType,
      chipValue: state.chipValue,
    }))
  );
  const isVirtualCareQuickCareSuppressed = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.VIRTUAL_CARE_UNET_QUICK_CARE
  );
  const shouldShowVirtualVisitAddress = (
    addressLine: string,
    suppressVirtualCareQuickCare: boolean
  ) =>
    suppressVirtualCareQuickCare &&
    isVirtualVisitOnlyAddress(chipValue, addressLine);

  return (
    <Card
      css={{
        'abyss-card-root': {
          border: 'none',
          boxShadow: 'none',
        },
      }}
    >
      <StyledDiv css={{ padding: '10px' }}>
        {selectedProviderList[index] ? (
          <div>
            <Box
              align="start"
              css={
                selected ? ShareBoxStyledSelectedMobile : ShareBoxStyledMobile
              }
              key={selectedProviderList[index].locationId}
            >
              <IconBrand
                css={{
                  '&.abyss-icon-root': {
                    width: '146px',
                    height: '112px',
                  },
                }}
                icon="heart"
                size="$xl"
              />
              <Flex direction="column">
                <Tooltip
                  align="center"
                  content={parseProviderName(
                    selectedProviderList[index].providerName,
                    selectedProviderList[index].providerType,
                    selectedProviderList[index].primaryDegrees?.[0],
                    getNameAttrs(selectedProviderList[index])
                  )}
                  position="top"
                >
                  <ShareBoxTextNameMobile
                    data-auto-testid="share-provider-name"
                    data-testid="share-provider-name"
                    onClick={() => handleProviderNameAndCarrotClick(index)}
                  >
                    {parseProviderName(
                      selectedProviderList[index].providerName,
                      selectedProviderList[index].providerType,
                      selectedProviderList[index].primaryDegrees?.[0],
                      {
                        firstName: selectedProviderList[index].firstName,
                        middleName: selectedProviderList[index].middleName,
                        lastName: selectedProviderList[index].lastName,
                      }
                    )}
                  </ShareBoxTextNameMobile>
                </Tooltip>
                <Flex>
                  {!shouldShowVirtualVisitAddress(
                    selectedProviderList[index].address?.line[0],
                    isVirtualCareQuickCareSuppressed
                  ) && (
                    <IconSymbol css={LocationIconMobile} icon="location_on" />
                  )}
                  <Tooltip
                    align="center"
                    content={t(selectedProviderList[index].address.line[0])}
                    position="top"
                  >
                    <ShareBoxTextAddressMobile>
                      {!shouldShowVirtualVisitAddress(
                        selectedProviderList[index].address?.line[0],
                        isVirtualCareQuickCareSuppressed
                      ) && t(selectedProviderList[index]?.address?.line?.[0])}
                    </ShareBoxTextAddressMobile>
                  </Tooltip>
                </Flex>
              </Flex>
              <ConfirmationModal
                closeButtonText={
                  t('COMPARE_DRAWER_CONFIRMATION_MODAL.VIEW_PROVIDER_BUTTON') ||
                  ''
                }
                dataForAnalytics={{
                  linkName: PRACTITIONER_NAME_FOR_ANALYTICS,
                  linkLocation: `modal:share card:position ${clickedIndex}`,
                  modalName: NEW_TAB_CONFIRMATION,
                  linkType: undefined,
                  customAttributesBlock,
                }}
                handleClickCb={() => handleClickCb()}
                isViewProvider
                locationForAnalytics={`modal:${NEW_TAB_CONFIRMATION}`}
                modal={modal}
                modalName="view-provider-modal"
              />
            </Box>
            <Layout.Group css={ButtonCss}>
              <Button
                aria-label={`${t(
                  'SHARE_DRAWER_TEXT.REMOVE'
                )} ${parseProviderName(
                  selectedProviderList[index]?.providerName,
                  selectedProviderList[index]?.providerType,
                  selectedProviderList[index]?.primaryDegrees?.[0],
                  {
                    firstName: selectedProviderList[index].firstName,
                    middleName: selectedProviderList[index].middleName,
                    lastName: selectedProviderList[index].lastName,
                  }
                )}`}
                data-auto-testid="button-remove"
                data-testid="button-remove"
                onClick={() => {
                  adobeLinkTrackEvent({
                    name: 'remove button',
                    location: `modal:share card:position ${index + 1}`,
                    type: 'internal',
                  });
                  removeItem(selectedProviderList[index].locationId);
                }}
                variant="text"
              >
                <IconSymbol
                  css={RemoveButtonIcon}
                  icon="remove_circle_outline"
                />
                <Text css={RemoveButtonText}>
                  {t('SHARE_DRAWER_TEXT.REMOVE')}
                </Text>
              </Button>
            </Layout.Group>
          </div>
        ) : (
          <Box
            css={ShareBoxStyledMobile}
            index={index}
            key={`share-card-skeleton${index}`}
          >
            <Text css={PlaceHolderText}>
              {t('SHARE_DRAWER_TEXT.CARD_PLACEHOLDER')}
            </Text>
          </Box>
        )}
      </StyledDiv>
    </Card>
  );
};
